import { flexRender, Table as ReactTable } from '@tanstack/react-table';
import { useEffect, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

import { cn } from '@/lib/tailwind-utils.js';
import { getCurrentRowSelectionCount } from '@/lib/bulk-selection.js';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { DefaultEmptyTable } from '@/components/default-empty-table.js';
import { TableHeaderSorting } from '@/components/table/table-header-sorting.js';
import { TableBulkActionsMenu } from '@/components/table/table-bulk-actions-menu.js';

export interface InfiniteScrollTableProps<RowData> {
  table: ReactTable<RowData>;
  filters?: {
    filterLeftComponents: ReactNode;
  };
  bulkActionsMenuItems?: ReactNode;
  topBarRightComponents?: ReactNode;
  topBarBottomComponents?: ReactNode;
  emptyState?: ReactNode;
  errorState?: ReactNode;
  loadingState?: ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  showTableHeader?: boolean;
  showRowSelection?: boolean;
  tableClassName?: string;
  rowClassName?: string;
  emptyStateClassName?: string;
  onSelectAllPagesChange?: (isSelectAllActive: boolean) => void;
  isSelectAllPagesActive?: boolean;
  selectedRowsCount?: number;
  totalRowCount?: number;
  onLoadMore?: () => void;
  scrollThreshold?: number;
  showLoadMoreIndicator?: boolean;
}

export interface InfiniteScrollTableRowSelectionProps<TData> {
  table: ReactTable<TData>;
  onSelectAllPagesChange?: (isSelectAllActive: boolean) => void;
  isSelectAllPagesActive?: boolean;
  totalRowCount?: number;
}

export function InfiniteScrollTableRowSelection<TData>({
  table,
  onSelectAllPagesChange,
  isSelectAllPagesActive = false,
  totalRowCount,
}: InfiniteScrollTableRowSelectionProps<TData>) {
  const selectedCount = table.getFilteredSelectedRowModel().rows.length;
  const totalCount = table.getFilteredRowModel().rows.length;
  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();

  const handleSelectAllPages = () => {
    if (isSelectAllPagesActive) {
      // Deselect all pages state and clear selection
      onSelectAllPagesChange?.(false);
      table.toggleAllRowsSelected(false);
    } else {
      // Activate select all pages state
      onSelectAllPagesChange?.(true);
    }
  };

  return (
    <div className="flex items-center justify-between px-2 mb-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {(selectedCount > 0 || isSelectAllPagesActive) && (
          <div className="flex items-center gap-2">
            <span>
              {isSelectAllPagesActive ? `All rows selected.` : `${selectedCount} of ${totalCount} row(s) selected.`}
            </span>
            {onSelectAllPagesChange && isAllPageRowsSelected && !isSelectAllPagesActive && (
              <button onClick={handleSelectAllPages} className="text-primary hover:underline font-medium">
                Select all {totalRowCount} rows
              </button>
            )}
            {onSelectAllPagesChange && isSelectAllPagesActive && (
              <button onClick={handleSelectAllPages} className="text-primary hover:underline font-medium">
                Deselect all {totalRowCount} rows
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function InfiniteScrollTable<RowData>({
  table,
  filters,
  bulkActionsMenuItems,
  topBarRightComponents,
  topBarBottomComponents,
  emptyState,
  errorState,
  loadingState = <LoaderIcon />,
  isLoading = false,
  hasError = false,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  showTableHeader = true,
  showRowSelection = false,
  tableClassName,
  rowClassName,
  emptyStateClassName,
  onSelectAllPagesChange,
  isSelectAllPagesActive = false,
  selectedRowsCount = 0,
  totalRowCount,
  onLoadMore,
  scrollThreshold = 200,
  showLoadMoreIndicator = true,
}: InfiniteScrollTableProps<RowData>) {
  const { ref, inView } = useInView({ threshold: 0, rootMargin: `${scrollThreshold}px` });

  // Trigger loading more when scroll reaches the bottom
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
      onLoadMore?.();
    }
  }, [inView, isFetchingNextPage, hasNextPage, fetchNextPage, onLoadMore]);

  // Select all rows on current page when isSelectAllPagesActive is true
  useEffect(() => {
    if (isSelectAllPagesActive) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [table, isSelectAllPagesActive, table.getState().pagination.pageIndex]);

  const rowCount = table.getRowModel().rows.length;
  const isEmpty = rowCount === 0;
  const columnCount = table.getVisibleFlatColumns().length;
  const renderTopBar = filters || bulkActionsMenuItems || topBarRightComponents || topBarBottomComponents;

  // Use table's selection count if available, otherwise use prop
  const actualSelectedRowsCount = bulkActionsMenuItems ? getCurrentRowSelectionCount(table) : selectedRowsCount;

  const resolvedEmptyState =
    emptyState == null ? (
      <DefaultEmptyTable />
    ) : typeof emptyState === 'string' ? (
      <DefaultEmptyTable>{emptyState}</DefaultEmptyTable>
    ) : (
      emptyState
    );

  const loadingComponent = (
    <TableRow>
      <TableCell colSpan={columnCount}>
        <div className="flex justify-center">{loadingState}</div>
      </TableCell>
    </TableRow>
  );

  const errorComponent = (
    <TableRow>
      <TableCell colSpan={columnCount}>
        <div className="flex justify-center">{errorState}</div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="h-full p-2 space-y-4 min-w-0 flex flex-col">
      {renderTopBar && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex flex-1 items-center space-x-2">{filters && filters.filterLeftComponents}</div>
            <div className="flex items-center space-x-1">
              {bulkActionsMenuItems && (
                <TableBulkActionsMenu
                  disabled={actualSelectedRowsCount === 0 && !isSelectAllPagesActive}
                  selectedRowsCount={actualSelectedRowsCount}
                  isSelectAllPagesActive={isSelectAllPagesActive}
                  totalRowCount={totalRowCount}
                >
                  {bulkActionsMenuItems}
                </TableBulkActionsMenu>
              )}
              {topBarRightComponents}
            </div>
          </div>
          {topBarBottomComponents && topBarBottomComponents}
        </>
      )}
      {showRowSelection && (
        <InfiniteScrollTableRowSelection
          table={table}
          onSelectAllPagesChange={onSelectAllPagesChange}
          isSelectAllPagesActive={isSelectAllPagesActive}
          totalRowCount={totalRowCount}
        />
      )}
      <div className="overflow-auto flex-1 min-h-0 relative bg-background">
        <div className={cn('rounded-md border min-w-max', showTableHeader && 'border-t-0', tableClassName)}>
          <Table>
            {showTableHeader && (
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const { column } = header;
                      return (
                        <TableHead key={header.id} className="whitespace-nowrap">
                          <div className="flex items-center gap-x-2 whitespace-nowrap">
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                            <TableHeaderSorting column={column} />
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
            )}
            <TableBody>
              {isLoading && loadingComponent}
              {!isLoading &&
                (hasError ? (
                  errorComponent
                ) : (
                  <>
                    {!isEmpty &&
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className={cn('whitespace-nowrap', rowClassName)}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    {showLoadMoreIndicator && isFetchingNextPage && (
                      <TableRow>
                        <TableCell colSpan={columnCount}>
                          <div className="flex justify-center py-4">{loadingState}</div>
                        </TableCell>
                      </TableRow>
                    )}
                    {/* Intersection observer target for infinite scroll */}
                    {hasNextPage && (
                      <TableRow ref={ref}>
                        <TableCell colSpan={columnCount} className="h-1 p-0" />
                      </TableRow>
                    )}
                  </>
                ))}
            </TableBody>
          </Table>
        </div>
        {isEmpty && !isLoading && (
          <div
            className={cn(
              'sticky left-0 right-0 top-1/2 -translate-y-1/2 z-50 mt-20 overflow-visible pointer-events-none',
              emptyStateClassName,
            )}
          >
            {resolvedEmptyState}
          </div>
        )}
      </div>
    </div>
  );
}
